import { Sailing } from "../models/Sailing"
import { Ship } from "../models/Ships"
import { StateroomTypeGrouping } from "../networking/fetchAvailability"

interface StateroomSubType {
  locations?: { [key: string]: { decks?: { [key: string]: { staterooms?: Array<{ stateroomId: string }> } } } }
  [key: string]: any
}

export const formatDate = (date: Date): string => {
    return date.toLocaleDateString()
}

export const capitalizeFirstLetter = (string: string): string => {
    return string.charAt(0).toUpperCase() + string.slice(1)
}

export const countAvailableRooms = (stateroomTypes: StateroomTypeGrouping | undefined): number => {
    return Object.entries(stateroomTypes ?? []).reduce((acc, [, value]) => acc + value.stateroomCount, 0)
}

export const formatDateRange = (startDate: string | undefined, endDate: string | undefined): string | undefined => {
  if (!startDate || !endDate) {
    return undefined
  }

  const start = new Date(startDate)
  const end = new Date(endDate)
  const startDateString = start.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })
  const endDateString = end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })
  return `${startDateString} - ${endDateString}`
}

export const formatSailDate = (sailDate: string): string => {
  return new Date(sailDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })
}

/*
 * The route and its stops, e.g. "Bahamian Cruise from Port Canaveral · Nassau,
 * Bahamas, Disney Castaway Cay". Disney prefixes every itinerary name with its
 * length, which the row already states, so drop it.
 */
export const formatItinerary = (sailing: Sailing): string => {
  const route = sailing.itinerary.replace(/^\d+-Night\s+/i, "")
  return sailing.portsOfCall.length > 0 ? `${route} · ${sailing.portsOfCall.join(", ")}` : route
}

const toDateKey = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${date.getFullYear()}-${month}-${day}`
}

/*
 * Sailings departing between today and the end of the month `monthsAhead` from
 * now. Dates are compared as YYYY-MM-DD strings so a traveller's time zone can
 * never shift a sailing into the wrong month.
 */
export const getUpcomingSailings = (sailings: Sailing[], monthsAhead = 2, today = new Date()): Sailing[] => {
  const start = toDateKey(today)
  const end = toDateKey(new Date(today.getFullYear(), today.getMonth() + monthsAhead + 1, 0))

  return sailings
    .filter((sailing) => sailing.sailDate >= start && sailing.sailDate <= end)
    .sort((a, b) => a.sailDate.localeCompare(b.sailDate) || a.shipName.localeCompare(b.shipName))
}

export const getSailingById = (sailings: Sailing[], sailingId: string): Sailing | undefined => {
  return sailings.find((sailing) => sailing.sailingId === sailingId)
}

export const getTotalRooms = (sailing: string) => {
  const ship = sailing.slice(0, 2)
  switch (ship) {
    case Ship.adventure:
      return 2111
    case Ship.wish:
    case Ship.treasure:
    case Ship.destiny:
      return 1256
    case Ship.dream:
    case Ship.fantasy:
      return 1250
    case Ship.magic:
    case Ship.wonder:
      return 875
    default:
      return 0
  }
}

export const getShipLogo = (sailing: string) => {
  const ship = sailing.slice(0, 2)
  switch (ship) {
    case Ship.adventure:
      return "https://cdn1.parksmedia.wdprapps.disney.com/vision-dam/digital/parks-platform/parks-global-assets/disney-cruise-line/logo/ShipLogo_DisneyAdventure.svg"
    case Ship.wish:
     return "https://cdn1.parksmedia.wdprapps.disney.com/vision-dam/digital/parks-platform/parks-global-assets/disney-cruise-line/logo/ShipLogo_DisneyWish.svg"
    case Ship.treasure:
      return "https://cdn1.parksmedia.wdprapps.disney.com/vision-dam/digital/parks-platform/parks-global-assets/disney-cruise-line/logo/ShipLogo_DisneyTreasure.svg"
    case Ship.destiny:
      return "https://cdn1.parksmedia.wdprapps.disney.com/vision-dam/digital/parks-platform/parks-global-assets/disney-cruise-line/logo/ShipLogo_DisneyDestiny.svg"
    case Ship.dream:
      return "https://cdn1.parksmedia.wdprapps.disney.com/vision-dam/digital/parks-platform/parks-global-assets/disney-cruise-line/logo/ShipLogo_DisneyDream.svg"
    case Ship.fantasy:
      return "https://cdn1.parksmedia.wdprapps.disney.com/vision-dam/digital/parks-platform/parks-global-assets/disney-cruise-line/logo/ShipLogo_DisneyFantasy.svg"
    case Ship.magic:
      return "https://cdn1.parksmedia.wdprapps.disney.com/vision-dam/digital/parks-platform/parks-global-assets/disney-cruise-line/logo/ShipLogo_DisneyMagic.svg"
    case Ship.wonder:
      return "https://cdn1.parksmedia.wdprapps.disney.com/vision-dam/digital/parks-platform/parks-global-assets/disney-cruise-line/logo/ShipLogo_DisneyWonder.svg"
    default:
      return null
  }
}

export const extractRoomNumbers = (subtype: StateroomSubType): string[] => {
  const roomNumbers: string[] = []
  
  if (!subtype.locations) {
    return roomNumbers
  }
  
  Object.values(subtype.locations).forEach((location) => {
    if (!location.decks) {
      return
    }
    
    Object.values(location.decks).forEach((deck) => {
      if (deck.staterooms && Array.isArray(deck.staterooms)) {
        deck.staterooms.forEach((stateroom) => {
          if (stateroom.stateroomId) {
            roomNumbers.push(stateroom.stateroomId)
          }
        })
      }
    })
  })
  
  return roomNumbers.sort()
}
